import { useEffect, useMemo, useState } from 'react'
import { Text } from '@chakra-ui/react'
import { CatalogoBasePage, type ColumnConfig, type FieldConfig } from '../../../components/CatalogoBasePage'
import { componentTypesService, componentCategoriesService, componentTiersService } from '../../../api/services'
import type { ComponentTypeItem, ComponentCategoryItem, ComponentTierItem } from '../../../api/models/maestros.models'
import { themeColors } from '../../../theme'

const baseColumns: ColumnConfig<ComponentTypeItem>[] = [
  { header: 'Código', render: (t) => <Text fontFamily="monospace" fontSize="sm" fontWeight="600" color={themeColors.text}>{t.code}</Text> },
  { header: 'Nombre', render: (t) => <Text fontSize="sm" color={themeColors.text}>{t.name}</Text> },
  { header: 'Categoría', render: (t) => <Text fontSize="sm" color={themeColors.textSecondary}>{t.categoryName ?? t.categoryCode ?? '-'}</Text> },
  { header: 'Nivel', render: (t) => <Text fontSize="sm" color={themeColors.textSecondary}>{t.tierName ?? t.tierCode ?? '-'}</Text> },
  { header: 'Descripción', render: (t) => <Text fontSize="sm" color={themeColors.textSecondary}>{t.description ?? '-'}</Text> },
  { header: 'Orden', render: (t) => <Text fontSize="sm" color={themeColors.textSecondary}>{t.sortOrder}</Text> },
]

export default function ComponentTypesPage() {
  const [categories, setCategories] = useState<ComponentCategoryItem[]>([])
  const [tiers, setTiers] = useState<ComponentTierItem[]>([])

  useEffect(() => {
    let cancelled = false
    componentCategoriesService
      .getAll({ pageSize: 200 })
      .then((res) => {
        if (!cancelled) setCategories(res.items)
      })
      .catch(() => { /* silent — picker just shows empty */ })
    componentTiersService
      .getAll({ pageSize: 200 })
      .then((res) => {
        if (!cancelled) setTiers(res.items)
      })
      .catch(() => { /* silent — picker just shows empty */ })
    return () => { cancelled = true }
  }, [])

  const fields = useMemo<FieldConfig<ComponentTypeItem>[]>(() => [
    { name: 'code', label: 'Código', type: 'text', required: true, placeholder: 'ej. bocamina' },
    { name: 'name', label: 'Nombre', type: 'text', required: true, placeholder: 'ej. Bocamina' },
    {
      name: 'categoryId',
      label: 'Categoría',
      type: 'select',
      required: true,
      options: categories.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: 'tierId',
      label: 'Nivel',
      type: 'select',
      required: true,
      options: tiers.map((tr) => ({ value: tr.id, label: tr.name })),
    },
    { name: 'description', label: 'Descripción', type: 'textarea', colSpan: 2 },
    { name: 'sortOrder', label: 'Orden', type: 'number', min: 0, step: 1 },
  ], [categories, tiers])

  return (
    <CatalogoBasePage<ComponentTypeItem>
      title="Tipos de Componente"
      subtitle="Catálogo único de tipos de componentes mineros (bocamina, tolva, garita, balanza, …)."
      entityName="tipo de componente"
      service={componentTypesService as never}
      columns={baseColumns}
      fields={fields}
      keyExtractor={(t) => t.id}
      idExtractor={(t) => t.id}
      searchPlaceholder="Buscar por código o nombre..."
      defaultValues={{ sortOrder: 99 }}
      createPermission="masters.write"
      updatePermission="masters.write"
      deletePermission="masters.write"
    />
  )
}
